import React, { useMemo } from 'react';
import { Offcanvas, Form, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useFilterContext } from 'providers/FilterProvider';

import SearchableInput from 'components/common/SearchableInput';

import { FILTER_TYPES } from 'constants/filterType';

const FilterPanel = ({ initialFilter, filterOptions, pageSize }) => {
  const { filterState, filterDispatch } = useFilterContext();
  const { showFilterPanel, draftFilters } = filterState;

  const handleFilterChange = (field, value) => {
    filterDispatch({
      type: 'SET_DRAFT_FILTER',
      payload: { field, value },
    });
  };

  const handleDateChange = (field, date) => {
    if (!date) {
      handleFilterChange(field, '');
      handleFilterChange(`${field}_display`, '');
      return;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    // display date (user select)
    handleFilterChange(`${field}_display`, dateString);

    // API request date
    if (field.endsWith('_after')) {
      handleFilterChange(field, `${dateString}T05:30:00`);
    } else if (field.endsWith('_before')) {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);

      const nextYear = nextDay.getFullYear();
      const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
      const nextDay_num = String(nextDay.getDate()).padStart(2, '0');

      handleFilterChange(
        field,
        `${nextYear}-${nextMonth}-${nextDay_num}T05:30:00`,
      );
    } else {
      // not before or after field
      handleFilterChange(field, dateString);
    }
  };

  const handleApplyFilters = () => {
    filterDispatch({ type: 'APPLY_FILTERS' });
  };

  const handleReset = () => {
    filterDispatch({
      type: 'RESET_FILTERS',
      payload: initialFilter,
    });
  };

  const handleClose = () => {
    filterDispatch({ type: 'CANCEL_FILTERS' });
  };

  // DatePicker display date
  const getDisplayDate = (field, value) => {
    if (!value) return null;

    // display 필드 존재
    if (draftFilters[`${field}_display`]) {
      return new Date(draftFilters[`${field}_display`]);
    }

    // display 필드 존재 X
    if (field.endsWith('_before')) {
      // before field
      const date = new Date(value.split('T')[0]);
      date.setDate(date.getDate() - 1);

      return date;
    } else {
      // not before field
      const datePart = value.split('T')[0];

      return new Date(datePart);
    }
  };

  const dateValidationErrors = useMemo(() => {
    const errors = {};

    const dateFieldGroups = filterOptions
      .filter(filter => filter.type === FILTER_TYPES.DATE)
      .reduce((groups, filter) => {
        if (filter.field.endsWith('_display')) return groups;

        const fieldBase = filter.field.replace(/_after$|_before$/, '');
        if (!groups[fieldBase]) {
          groups[fieldBase] = [];
        }
        groups[fieldBase].push(filter.field);
        return groups;
      }, {});

    Object.entries(dateFieldGroups).forEach(([baseField, fields]) => {
      const afterField = fields.find(f => f.endsWith('_after'));
      const beforeField = fields.find(f => f.endsWith('_before'));

      if (
        afterField &&
        beforeField &&
        draftFilters[afterField] &&
        draftFilters[beforeField]
      ) {
        const getDisplayDateStr = field => {
          if (draftFilters[`${field}_display`]) {
            return new Date(draftFilters[`${field}_display`]);
          }

          // display field X
          if (field.endsWith('_before')) {
            const date = new Date(draftFilters[field].split('T')[0]);
            date.setDate(date.getDate() - 1);

            return new Date(date.toISOString().split('T')[0]);
          } else {
            return new Date(draftFilters[field].split('T')[0]);
          }
        };

        const afterDate = getDisplayDateStr(afterField);
        const beforeDate = getDisplayDateStr(beforeField);

        if (afterDate > beforeDate) {
          errors[afterField] = 'Start date must be earlier than end date';
          errors[beforeField] = 'End date must be later than start date';
        }

        const MAX_DATE_GAP = 31;

        const diffTime = Math.abs(beforeDate - afterDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > MAX_DATE_GAP) {
          errors[afterField] = `Date range cannot exceed ${MAX_DATE_GAP} days`;
          errors[beforeField] = `Date range cannot exceed ${MAX_DATE_GAP} days`;
        }
      }
    });

    return errors;
  }, [draftFilters, filterOptions]);

  const hasValidationErrors = Object.keys(dateValidationErrors).length > 0;

  // filter 타입에 맞는 input 리턴
  const renderFilterInput = filter => {
    switch (filter.type) {
      case FILTER_TYPES.SELECT:
        return (
          <Form.Select
            size="sm"
            value={
              filter.field === 'page_size'
                ? draftFilters[filter.field] || String(pageSize)
                : draftFilters[filter.field] || ''
            }
            onChange={e => handleFilterChange(filter.field, e.target.value)}
            className="mb-3"
          >
            {filter.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );
      case FILTER_TYPES.MULTI_SELECT:
        return (
          <div className="d-flex flex-column">
            {filter.options
              .filter(opt => opt.value)
              .map(option => (
                <Form.Check
                  key={option.value}
                  className="mb-md-0"
                  type="checkbox"
                  id={option.value}
                  label={option.label}
                  checked={
                    Array.isArray(draftFilters[filter.field]) &&
                    draftFilters[filter.field].includes(option.value)
                  }
                  onChange={e => {
                    const currentValues = Array.isArray(
                      draftFilters[filter.field],
                    )
                      ? [...draftFilters[filter.field]]
                      : [];

                    if (e.target.checked) {
                      handleFilterChange(filter.field, [
                        ...currentValues,
                        option.value,
                      ]);
                    } else {
                      handleFilterChange(
                        filter.field,
                        currentValues.filter(value => value !== option.value),
                      );
                    }
                  }}
                />
              ))}
          </div>
        );
      case FILTER_TYPES.MULTI_TEXT:
        return (
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            placeholder={`Enter ${filter.label} separated by lines`}
            value={draftFilters[filter.field] || ''}
            onChange={e => handleFilterChange(filter.field, e.target.value)}
            className="mb-3"
          />
        );
      case FILTER_TYPES.DATE:
        return (
          <>
            <DatePicker
              selected={getDisplayDate(
                filter.field,
                draftFilters[filter.field],
              )}
              onChange={date => handleDateChange(filter.field, date)}
              dateFormat="yyyy-MM-dd"
              isClearable
              placeholderText="Select date"
              className={`form-control form-control-sm ${
                dateValidationErrors[filter.field] ? 'border-danger' : ''
              }`}
            />
            {dateValidationErrors[filter.field] && (
              <small className="text-danger d-block mt-1">
                {dateValidationErrors[filter.field]}
              </small>
            )}
          </>
        );
      case FILTER_TYPES.SEARCH:
        return (
          <SearchableInput
            apiService={filter.apiService}
            searchField={filter.searchField}
            displayField={filter.displayField || 'name'}
            valueField={filter.valueField}
            value={draftFilters[filter.field] || ''}
            onChange={value => handleFilterChange(filter.field, value)}
            onSelect={(item, value) =>
              handleFilterChange(
                filter.field,
                value ?? item[filter.displayField || 'name'],
              )
            }
            placeholder={`Search ${filter.label}`}
            renderItem={filter.renderItem}
            visibleItems={2}
          />
        );
      case FILTER_TYPES.TEXT:
      default:
        return (
          <Form.Control
            type="text"
            size="sm"
            placeholder={`Search by ${filter.label}`}
            value={draftFilters[filter.field] || ''}
            onChange={e => handleFilterChange(filter.field, e.target.value)}
            className="mb-3"
          />
        );
    }
  };

  return (
    <Offcanvas
      show={showFilterPanel}
      onHide={handleClose}
      placement="end"
      style={{ maxWidth: '22rem' }}
      className="border-0"
    >
      <Offcanvas.Header
        closeButton
        closeVariant="white"
        className="bg-shape settings-panel-header"
      >
        <Offcanvas.Title as="div" className="py-1 z-1 light">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h5 className="text-white">
              <FontAwesomeIcon icon="filter" className="me-2 fs-9" />
              Filter Options
            </h5>
            <Button
              variant="primary"
              size="sm"
              className="rounded-pill mt-0 mb-0"
              style={{ fontSize: '12px' }}
              onClick={handleReset}
            >
              <FontAwesomeIcon
                icon="redo-alt"
                style={{ fontSize: '10px' }}
                className="me-1"
              />
              Reset
            </Button>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="scrollbar">
        <Form
          onSubmit={e => {
            e.preventDefault();
            handleApplyFilters();
          }}
        >
          {filterOptions.map(filter => (
            <div key={filter.field} className="mb-3">
              <Form.Label className="fs-9">{filter.label}</Form.Label>
              {renderFilterInput(filter)}
            </div>
          ))}

          <div className="mt-4 d-grid">
            <Button
              type="submit"
              variant="primary"
              className="py-2"
              disabled={hasValidationErrors}
            >
              <FontAwesomeIcon icon="search" className="me-2" />
              Apply Filters
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default FilterPanel;
